import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import partition from 'lodash/partition';
import startsWith from 'lodash/startsWith';
import { useDocumentBinding } from '@xmatters/react';

import { isFromCocoon as isCocoonPage } from 'utils/navigation';
import { usePageTitle } from 'hooks/usePageTitle';
import { useIntl } from 'react-intl';
import MainNavItem from './MainNavItem';
import SubNavMenu from './SubNavMenu';
import WideModeToggle, { getStoredToggledWideState } from './WideModeToggle';
import {
  NavigationId,
  CocoonMainMenuIdToMenuIdMap,
  CocoonSubMenuIdToHelpIdMap,
} from './constants';

import styles from './MainNav.module.scss';

// TODO: for _most cases_ of new React pages, the page itself running a hook with this type of data would probably work better.
function getNavigationDetails(pathnameProp) {
  if (isCocoonPage()) {
    const mainMenuId = get(window, 'PageParameters.mainMenuId');
    const subMenuId = get(window, 'PageParameters.subMenuId');
    let helpId = CocoonSubMenuIdToHelpIdMap[subMenuId];

    if (pathnameProp?.includes('EditPhoneRecording')) {
      // Override helpId from PhoneRecordings to EditPhoneRecording
      helpId = CocoonSubMenuIdToHelpIdMap.EditPhoneRecording;
    }

    return {
      menuId: CocoonMainMenuIdToMenuIdMap[mainMenuId],
      subMenuId,
      helpId,
    };
  }
  if (!pathnameProp) {
    return null;
  }

  const pathname = pathnameProp.startsWith('/xmatters/ui')
    ? pathnameProp.replace('/xmatters/ui', '')
    : pathnameProp;

  if (pathname.includes('UserPerformance')) {
    return {
      menuId: NavigationId.USERS,
      subMenuId: 'UserPerformanceReportPlace',
      helpId: 'UserPerformanceReportPlace',
    };
  }
  if (pathname.includes('GroupPerformance')) {
    return {
      menuId: NavigationId.GROUPS,
      subMenuId: 'GroupPerformance',
      helpId: 'GroupPerformanceReportPlace',
    };
  }
  if (pathname.includes('alerts')) {
    return {
      menuId: NavigationId.REPORTS,
      subMenuId: 'alerts',
      helpId: 'AlertsReport',
    };
  }
  if (pathname.includes('AlertViews')) {
    return {
      menuId: NavigationId.REPORTS,
      subMenuId: 'AlertViews',
      helpId: 'AlertsReportViews',
    };
  }
  if (pathname.includes('notifications')) {
    return {
      menuId: NavigationId.REPORTS,
      subMenuId: 'notifications',
      helpId: 'CompanyNotificationReportPlace',
    };
  }
  if (startsWith(pathname, '/incidents/playbooks')) {
    return {
      menuId: NavigationId.INCIDENTS,
      subMenuId: 'IncidentPlaybooks',
      helpId: 'IncidentPlaybooks',
    };
  }
  if (startsWith(pathname, '/incidents/incident-task-list')) {
    return {
      menuId: NavigationId.INCIDENTS,
      subMenuId: 'IncidentTaskLists',
      helpId: 'IncidentTaskLists',
    };
  }
  if (startsWith(pathname, '/incidents/incident-types')) {
    return {
      menuId: NavigationId.INCIDENTS,
      subMenuId: 'IncidentTypes',
      helpId: 'IncidentTypes',
    };
  }
  if (startsWith(pathname, '/incidents')) {
    return {
      menuId: NavigationId.INCIDENTS,
      subMenuId: null,
      helpId: 'IncidentsPlace',
    };
  }
  if (startsWith(pathname, '/services')) {
    return {
      menuId: 'SERVICES',
      subMenuId: null,
      helpId: 'Services',
    };
  }
  if (startsWith(pathname, '/reports/signals')) {
    return {
      menuId: 'REPORTS',
      subMenuId: 'SignalsReport',
      helpId: 'SignalsReport',
    };
  }
  if (pathname.includes('DynamicTeams')) {
    return {
      menuId: NavigationId.GROUPS,
      subMenuId: null,
      helpId: 'DynamicTeams',
    };
  }
  if (pathname.includes('MessagingList')) {
    return {
      menuId: NavigationId.MESSAGING,
      subMenuId: null,
      helpId: 'MessagingList',
    };
  }
  if (pathname.includes('workflowsaudit')) {
    return {
      menuId: NavigationId.REPORTS,
      subMenuId: 'WorkflowsAuditReport',
      helpId: 'WorkflowsAuditReport',
    };
  }
  if (pathname.includes('systemaudit')) {
    return {
      menuId: NavigationId.REPORTS,
      subMenuId: 'SystemAuditReport',
      helpId: 'SystemAuditReport',
    };
  }
  if (startsWith(pathname, '/UserPropertiesPage')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'UserPropertiesPage',
      helpId: 'UserPropertiesPage',
    };
  }
  if (startsWith(pathname, '/DeviceManagementPlace')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'DeviceManagementPlace',
      helpId: 'DeviceManagementPlace',
    };
  }
  if (startsWith(pathname, '/DeviceNameDetailPlace')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'DeviceManagementPlace',
      helpId: 'DeviceNameDetailPlace',
    };
  }
  if (startsWith(pathname, '/SecuritySettings')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'SecuritySettings',
      helpId: 'SecuritySettingsPage',
    };
  }
  if (startsWith(pathname, '/LocationsAndLanguages')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'LocationsAndLanguages',
      helpId: 'LocationsAndLanguages',
    };
  }
  if (startsWith(pathname, '/SitesPlace')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'SitesPlace',
      helpId: 'SitesPlace',
    };
  }
  if (startsWith(pathname, '/Holidays')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'Holidays',
      helpId: 'CompanyHolidaysList',
    };
  }

  if (startsWith(pathname, '/ExternalDataPage')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'ExternalDataPage',
      helpId: 'ExternalDataPage',
    };
  }

  if (startsWith(pathname, '/ExternalConferenceBridgeList')) {
    return {
      menuId: NavigationId.DEVELOPER,
      subMenuId: 'conference-bridges',
      helpId: 'ExternalConferenceBridgeListPlace',
    };
  }
  if (pathname.includes('PlanUpgrade')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'PlanOverviewPlace',
    };
  }

  if (pathname.includes('GeneralSettingsPage')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'BasicCompanyDetailsPlace',
    };
  }

  if (pathname.includes('LiveCallRoutingPage')) {
    return {
      menuId: NavigationId.ADMIN,
      subMenuId: 'LiveCallRoutingPage',
      helpId: 'LiveCallRoutingPage',
    };
  }

  if (pathname.includes('LiveCallRoutingReport')) {
    return {
      menuId: NavigationId.REPORTS,
      subMenuId: 'LiveCallRoutingReport',
      helpId: 'LiveCallRoutingReport',
    };
  }

  if (startsWith(pathname, '/OauthCredentialsPage')) {
    return {
      menuId: NavigationId.DEVELOPER,
      subMenuId: 'oauth-credentials-page',
      helpId: 'OauthCredentialsPlace',
    };
  }
  if (startsWith(pathname, '/VoiceRecordingListPage')) {
    return {
      menuId: NavigationId.DEVELOPER,
      subMenuId: 'voice-recordings',
      helpId: 'VoiceRecordingsPlace',
    };
  }
  if (startsWith(pathname, '/VoiceRecordingListPage')) {
    return {
      menuId: NavigationId.DEVELOPER,
      subMenuId: 'voice-recordings',
      helpId: 'VoiceRecordingsPlace',
    };
  }
  if (pathname.includes('workflow:')) {
    return {
      menuId: NavigationId.DEVELOPER,
      subMenuId: 'WorkflowListPlace',
    };
  }
  if (pathname.includes('TtsDictionary')) {
    return {
      menuId: NavigationId.DEVELOPER,
      subMenuId: 'tts-dictionary-page',
      helpId: 'TTSDictionaryPlace',
    };
  }
  if (pathname.includes('VoiceRecordingListPage')) {
    return {
      menuId: NavigationId.DEVELOPER,
      subMenuId: 'voice-recordings',
      helpId: 'VoiceRecordingsPlace',
    };
  }
  return null;
}

const MainNav = ({ navItems, updateNavigation, history, location }) => {
  const [openItemId, setOpenItemId] = useState();
  const [isWideMode, setIsWideMode] = useState(
    getStoredToggledWideState() === 'true',
  );
  const ref = useRef();
  const setOpenItem = item => setOpenItemId(item?.id || null);

  // Close if user clicks outside the menu:
  useDocumentBinding('click', event => {
    const { current } = ref;
    const { target } = event;

    if (!current) return;

    if (current !== target && !current.contains(target)) {
      setOpenItem(null);
    }
  });

  useEffect(() => {
    const updatePureReactRouteNav = loc => {
      const navUpdates = getNavigationDetails(loc.pathname);
      if (navUpdates) {
        updateNavigation(navUpdates);
      } else if (window.xmGwtUpdateNavigationState) {
        window.xmGwtUpdateNavigationState();
      }
    };

    updatePureReactRouteNav(location);

    return history.listen(newLocation => {
      updatePureReactRouteNav(newLocation);
    });
  }, [history, location, updateNavigation]);

  const [[adminItem], mainNavItems] = partition(navItems, {
    id: NavigationId.ADMIN,
  });
  const className = classnames(styles.mainNav, {
    [styles.wideMode]: isWideMode,
  });

  const intl = useIntl();
  usePageTitle(null, intl);

  return (
    <nav
      className={className}
      ref={ref}
      data-testid="mainNav"
      data-pendo-guide-id="DASHBOARD_MAIN_NAV"
    >
      <div className={styles.navContent}>
        <div className={styles.mainItems}>
          {mainNavItems.map(item => {
            const isSelected = openItemId === item.id;
            return (
              <div key={item.id}>
                <MainNavItem
                  item={item}
                  isSelected={isSelected}
                  selectItem={setOpenItem}
                  isWideMode={isWideMode}
                />
                {isSelected && (
                  <SubNavMenu
                    nav={item}
                    onClose={() => {
                      setOpenItem(null);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        {adminItem && (
          <>
            <MainNavItem
              item={adminItem}
              isSelected={openItemId === adminItem.id}
              selectItem={setOpenItem}
              isWideMode={isWideMode}
            />
            {openItemId === adminItem.id && (
              <SubNavMenu
                nav={adminItem}
                onClose={() => {
                  setOpenItem(null);
                }}
              />
            )}
          </>
        )}
      </div>
      <WideModeToggle onChange={setIsWideMode} isWideMode={isWideMode} />
    </nav>
  );
};

MainNav.displayName = 'MainNav';
MainNav.propTypes = {
  navItems: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};

export default MainNav;
